import { Checkbox, DatePicker, Form, Input, Radio, Select, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import CommonModalFactory from '@/plugins/ModalFactory';
import { analysisApi, EAnalysisReportType, ScheduledReportPackage, ScheduledReportPlan } from '@/axios/analysis';

dayjs.extend(utc);

type FormValues = {
  taskName: string;
  startMode: 'KEEP' | 'IMMEDIATE' | 'AT_TIME';
  firstRunTime?: Dayjs;
  intervalDays: string;
  dataSource: 'COMPANY_REPORT' | 'CLIENT_INDUSTRY_BRAND' | 'CLIENT_DATA_DASHBOARD';
  timeRange: 'ALL' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'LATEST_1_DAY';
  packageIds: string[];
  reportCycle: EAnalysisReportType;
  reportAutoPublishToClient: 'ENABLE' | 'DISABLE';
  reportAutoUpdateClientReport: 'ENABLE' | 'DISABLE';
};

const sourceOptions = [
  { value: 'COMPANY_REPORT', label: '客户报表' },
  { value: 'CLIENT_INDUSTRY_BRAND', label: '客户后台（行业品牌看板）' },
  { value: 'CLIENT_DATA_DASHBOARD', label: '客户后台（报表）' },
];

export const openScheduledReportModal = CommonModalFactory<{ companyId: string; plan?: ScheduledReportPlan & { packageIds: string[] } }>(
  { title: '新建定时分析报告', width: 500, maskClosable: false },
  ({ data, setOkFn, update }) => {
    const [form] = Form.useForm<FormValues>();
    const [packages, setPackages] = useState<ScheduledReportPackage[]>([]);
    const [loading, setLoading] = useState(false);
    const submission = useRef({ payload: '', key: uuid(), pending: false });
    const source = Form.useWatch('dataSource', form);
    useEffect(() => {
      let cancelled = false;
      setLoading(true);
      const load = async () => {
        const all: ScheduledReportPackage[] = [];
        for (let pageIndex = 1; !cancelled; pageIndex++) {
          const res = await analysisApi.scheduledPackages({ companyId: data.companyId, pageIndex, pageSize: 100 });
          all.push(...res.records);
          if (all.length >= res.total || !res.records.length) break;
        }
        if (!cancelled) setPackages(all);
      };
      void load().catch(() => {}).finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }, [data.companyId]);
    useEffect(() => {
      if (source && source !== 'CLIENT_INDUSTRY_BRAND') form.setFieldValue('timeRange', 'ALL');
      else if (source === 'CLIENT_INDUSTRY_BRAND' && form.getFieldValue('timeRange') === 'ALL') form.setFieldValue('timeRange', undefined);
    }, [source, form]);
    useEffect(() => {
      setOkFn(async () => {
        if (submission.current.pending) throw new Error('正在提交');
        const values = await form.validateFields();
        const startMode = data.plan ? values.startMode || 'KEEP' : form.isFieldTouched('firstRunTime') ? 'AT_TIME' : 'IMMEDIATE';
        const request = {
          companyId: data.companyId,
          taskName: values.taskName.trim(),
          startMode,
          firstRunTime: startMode === 'AT_TIME' ? values.firstRunTime?.format('YYYY-MM-DDTHH:mm:ss') : null,
          intervalDays: Number(values.intervalDays),
          dataSource: values.dataSource,
          timeRange: values.timeRange,
          packageIds: [...values.packageIds].sort(),
          reportCycle: values.reportCycle,
          reportAutoPublishToClient: values.reportAutoPublishToClient,
          reportAutoUpdateClientReport: values.reportAutoUpdateClientReport,
        };
        const payload = JSON.stringify(request);
        if (submission.current.payload !== payload) submission.current.key = uuid();
        submission.current.payload = payload;
        submission.current.pending = true;
        update({ confirmLoading: true });
        try {
          if (data.plan) {
            await analysisApi.updateScheduled({ ...request, planId: data.plan.planId,
              startMode: startMode === 'KEEP' ? undefined : startMode });
          } else {
            await analysisApi.createScheduled({ ...request, startMode: startMode === 'KEEP' ? 'IMMEDIATE' : startMode }, submission.current.key);
          }
          message.success(data.plan ? '定时任务已更新' : '定时报告任务创建成功');
        } catch (error) {
          const messageText = typeof error === 'object' && error && 'err_msg' in error
            ? String((error as { err_msg?: string }).err_msg || '') : '';
          const [field, detail] = messageText.split('：', 2);
          const fields: Array<keyof FormValues> = ['taskName', 'firstRunTime', 'intervalDays', 'dataSource', 'timeRange', 'packageIds'];
          if (detail && fields.includes(field as keyof FormValues)) {
            form.setFields([{ name: field as keyof FormValues, errors: [detail] }]);
          }
          throw error;
        } finally {
          submission.current.pending = false;
          update({ confirmLoading: false });
        }
      });
    }, [data.companyId, data.plan, form, setOkFn, update]);
    return <Form form={form} layout="vertical" initialValues={{ reportCycle: EAnalysisReportType.Unset, reportAutoPublishToClient: 'DISABLE', reportAutoUpdateClientReport: 'DISABLE', ...(data.plan ? { ...data.plan, startMode: 'KEEP', intervalDays: String(data.plan.intervalDays), firstRunTime: dayjs(data.plan.nextRunTime) } : { startMode: 'AT_TIME', firstRunTime: dayjs().utcOffset(480), intervalDays: '7' }) }}>
      <Form.Item name="taskName" label="定时任务名称" rules={[{ required: true, whitespace: true, message: '定时任务名称不能为空' }, { max: 100, message: '定时任务名称不能超过100个字符' }]}>
        <Input maxLength={100} placeholder="请输入定时任务名称" />
      </Form.Item>
      <Form.Item name="reportCycle" label="报告类型">
        <Radio.Group options={[
          { value: EAnalysisReportType.Unset, label: '未设置' },
          { value: EAnalysisReportType.Daily, label: '日报' },
          { value: EAnalysisReportType.Weekly, label: '周报' },
          { value: EAnalysisReportType.Monthly, label: '月报' },
        ]} />
      </Form.Item>
      <Form.Item label="分析报告生成后自动发布" extra="可多选，报告生成成功后自动执行所选操作。">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <Form.Item name="reportAutoPublishToClient" noStyle
            getValueProps={(value) => ({ checked: value === 'ENABLE' })}
            getValueFromEvent={(event) => event.target.checked ? 'ENABLE' : 'DISABLE'}>
            <Checkbox>发布到客户后台</Checkbox>
          </Form.Item>
          <Form.Item name="reportAutoUpdateClientReport" noStyle
            getValueProps={(value) => ({ checked: value === 'ENABLE' })}
            getValueFromEvent={(event) => event.target.checked ? 'ENABLE' : 'DISABLE'}>
            <Checkbox>更新到客户报表</Checkbox>
          </Form.Item>
        </div>
      </Form.Item>
      {data.plan && <Form.Item name="startMode" label="后续执行时间" extra="保存后等待执行和等待重试的旧记录将失效，按新配置执行后续周期。">
        <Radio.Group options={[{ value: 'KEEP', label: '沿用原时间起点' }, { value: 'IMMEDIATE', label: '立即执行' }, { value: 'AT_TIME', label: '指定时间' }]} />
      </Form.Item>}
      <div style={{ display: 'flex', gap: 12, alignItems: 'start' }}>
        <div style={{ flex: '1 1 60%', minWidth: 0 }}>
          <Form.Item noStyle shouldUpdate={(prev, next) => prev.startMode !== next.startMode}>
            {({ getFieldValue }) => (!data.plan || getFieldValue('startMode') === 'AT_TIME') ? <Form.Item name="firstRunTime" label={data.plan ? '指定时间' : '定时'} rules={[{ required: true, message: '请选择起始时间' }, { validator: (_, value) => !value || (!data.plan && !form.isFieldTouched('firstRunTime')) || value.isAfter(dayjs().utcOffset(480), 'second')
              ? Promise.resolve() : Promise.reject(new Error('起始时间不能早于当前时间')) }]}>
              <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} disabledDate={(d) => d.isBefore(dayjs().utcOffset(480).startOf('day'), 'day')} />
            </Form.Item> : null}
          </Form.Item>
        </div>
        <Form.Item name="intervalDays" label="每隔" style={{ flex: '1 1 40%', minWidth: 0 }} rules={[{ validator: (_, value) => /^(?:[1-9]\d*)$/.test(value) && Number.isSafeInteger(Number(value))
          ? Promise.resolve() : Promise.reject(new Error('周期天数必须大于等于1')) }]}>
          <Input inputMode="numeric" addonAfter="天" aria-label="执行周期" />
        </Form.Item>
      </div>
      <Form.Item name="dataSource" label="数据来源" rules={[{ required: true, message: '请选择报告的数据来源' }]}>
        <Radio.Group options={sourceOptions} style={{ display: 'flex', flexDirection: 'column', gap: 8 }} />
      </Form.Item>
      <Form.Item name="timeRange" label="时间选择" rules={[{ required: true, message: '请选择时间范围' }]}>
        <Radio.Group options={[
          { value: 'ALL', label: '全部', disabled: source === 'CLIENT_INDUSTRY_BRAND' },
          { value: 'LAST_30_DAYS', label: '近30天', disabled: !!source && source !== 'CLIENT_INDUSTRY_BRAND' },
          { value: 'LAST_7_DAYS', label: '近7天', disabled: !!source && source !== 'CLIENT_INDUSTRY_BRAND' },
          { value: 'LATEST_1_DAY', label: '最新1天', disabled: !!source && source !== 'CLIENT_INDUSTRY_BRAND' },
        ]} />
      </Form.Item>
      <Form.Item name="packageIds" label="词包" rules={[{ required: true, type: 'array', min: 1, message: '至少选择一个词包' }]}>
        <Select mode="multiple" loading={loading} placeholder="请选择词包" optionFilterProp="label" options={[...packages, ...(data.plan?.packages || []).filter(p => !packages.some(item => item.recordId === p.recordId))].map((p) => ({ value: p.recordId, label: p.name }))} />
      </Form.Item>
    </Form>;
  },
);
